// Gestion du consentement aux traceurs (article 82 de la loi Informatique et
// Libertes, RGPD, recommandation CNIL 2020-092).
//
// Regles implementees ici :
//   - aucun traceur non essentiel n'est depose avant un choix explicite ;
//   - le choix est conserve 6 mois (duree recommandee par la CNIL), refus
//     compris : on ne redemande pas a chaque page ;
//   - le consentement est versionne : si la liste des finalites change,
//     CONSENT_VERSION est incrementee et le choix est redemande ;
//   - la date du choix est stockee pour servir de preuve.
//
// Les traceurs strictement necessaires (cookie "auth", token en localStorage,
// panier) sont exemptes de consentement et ne sont donc pas pilotes ici.

import { Delegate } from "../Tools/Delegate";

/** Finalites soumises au consentement. "necessary" est implicite et non listee. */
export type ConsentCategory = "analytics" | "media";

export const consentCategories: ConsentCategory[] = ["analytics", "media"];

/** Etat persiste dans le cookie de consentement. */
export interface ConsentState {
    /** Version des finalites au moment du choix. */
    version: number;
    /** Date ISO du choix (preuve du consentement). */
    date: string;
    /** Choix par finalite. */
    choices: Record<ConsentCategory, boolean>;
}

/** Nom du cookie de preuve : lisible cote serveur comme cote client. */
export const consentCookieName = "ev_consent";

/**
 * A incrementer des qu'une finalite est ajoutee ou que le perimetre d'un
 * traceur change : le bandeau sera de nouveau presente a tous les visiteurs.
 */
export const CONSENT_VERSION = 1;

/** Duree de conservation du choix, refus compris (recommandation CNIL). */
const consentDurationDays = 182;

/** Declenche a chaque enregistrement d'un choix, avec l'etat resultant. */
export const OnConsentChanged = new Delegate();

function readCookie(name: string): string | null {
    const prefix = `${name}=`;
    const found = document.cookie
        .split(";")
        .map((part) => part.trim())
        .find((part) => part.startsWith(prefix));

    return found ? decodeURIComponent(found.substring(prefix.length)) : null;
}

function writeCookie(name: string, value: string, days: number): void {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    const secure = location.protocol === "https:" ? "; Secure" : "";

    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
}

function deleteCookie(name: string, domain?: string): void {
    const scope = domain ? `; domain=${domain}` : "";
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${scope}`;
}

/** Choix par defaut : tout refuse tant que l'utilisateur n'a pas tranche. */
export function defaultChoices(): Record<ConsentCategory, boolean> {
    return { analytics: false, media: false };
}

/**
 * Lit le choix enregistre. Retourne null si aucun choix valide n'existe
 * (jamais choisi, cookie corrompu, ou version de finalites obsolete).
 */
export function readConsent(): ConsentState | null {
    const raw = readCookie(consentCookieName);

    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw) as Partial<ConsentState>;

        if (parsed.version !== CONSENT_VERSION || !parsed.choices) return null;

        const choices = defaultChoices();
        for (const category of consentCategories) {
            choices[category] = parsed.choices[category] === true;
        }

        return { version: CONSENT_VERSION, date: parsed.date ?? "", choices };
    } catch {
        return null;
    }
}

/** True tant que le visiteur n'a pas fait de choix : le bandeau doit s'afficher. */
export function isChoiceRequired(): boolean {
    return readConsent() === null;
}

/** True si la finalite a ete explicitement acceptee. */
export function hasConsent(category: ConsentCategory): boolean {
    return readConsent()?.choices[category] === true;
}

/**
 * Enregistre le choix, purge les traceurs des finalites refusees et notifie
 * les abonnes (activation de GTM, deblocage des iframes...).
 */
export function saveConsent(choices: Partial<Record<ConsentCategory, boolean>>): ConsentState {
    const resolved = defaultChoices();
    for (const category of consentCategories) {
        resolved[category] = choices[category] === true;
    }

    const state: ConsentState = {
        version: CONSENT_VERSION,
        date: new Date().toISOString(),
        choices: resolved,
    };

    writeCookie(consentCookieName, JSON.stringify(state), consentDurationDays);

    if (!resolved.analytics) clearAnalyticsCookies();

    OnConsentChanged.Fire(state);

    return state;
}

/** Accepte toutes les finalites. */
export function acceptAll(): ConsentState {
    return saveConsent({ analytics: true, media: true });
}

/** Refuse toutes les finalites non essentielles. */
export function rejectAll(): ConsentState {
    return saveConsent(defaultChoices());
}

/**
 * Supprime les cookies Google Analytics deposes avant un retrait de
 * consentement. Ils sont poses sur le domaine et le domaine parent : on tente
 * les deux, un cookie deja absent est simplement ignore par le navigateur.
 */
function clearAnalyticsCookies(): void {
    const parentDomain = `.${location.hostname.split(".").slice(-2).join(".")}`;

    document.cookie
        .split(";")
        .map((part) => part.trim().split("=")[0])
        .filter((name) => /^(_ga|_gid|_gat|_gac_)/.test(name))
        .forEach((name) => {
            deleteCookie(name);
            deleteCookie(name, location.hostname);
            deleteCookie(name, parentDomain);
        });
}

/** Efface le choix : le bandeau sera represente au prochain chargement. */
export function resetConsent(): void {
    deleteCookie(consentCookieName);
    clearAnalyticsCookies();
}
